// Sidebar — chat navigation rail.
//
// Composes, top to bottom: a one-row Activity bar and the categorised room list. The room
// list itself lives in `RoomList`; this component is a thin shell that
// owns width, background, and activity rows that sit above rooms.
//
// Visuals follow docs/chat-design-brief.md: token-driven surfaces and
// type. The component reads `--chat-*` tokens from
// `@nurion/theme/chat.css`; no hardcoded px or hex colours.

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useNurionTheme } from "@nurion/theme/react";
import { SidebarUser, type SidebarUserProps } from "@nurion/ui/sidebar-user";
import { toSidebarProducts } from "@nurion/ui/products/widget";
import { Archive, KeyRound, Smile } from "lucide-react";

import { ActivityBar } from "~/components/ActivityBar";
import { DndIcon } from "~/components/DndIcon";
import { DndMenu } from "~/components/DndMenu";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import { IconTooltip } from "~/components/IconTooltip";
import { StatusPicker } from "~/components/StatusPicker";
import { RoomList } from "~/components/RoomList";
import { useEmbedRuntime } from "~/context/EmbedRuntimeContext";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useAttention } from "~/hooks/useAttention";
import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { useDnd } from "~/hooks/useDnd";
import { useMyStatus } from "~/hooks/useMyStatus";
import { useScheduledMessages } from "~/hooks/useScheduledMessages";
import { useInvitations } from "~/hooks/useInvitations";
import { useOwnMemberNames } from "~/hooks/useOwnMemberNames";
import {
  localpartOf,
  mostFrequentName,
  preferredAccountLabel,
  resolveAccountLabel,
} from "~/lib/account-label";
import { getInitials } from "~/lib/initials";
import { persistWidth, readPersistedWidth, readWidthBounds } from "~/lib/panel-width";
import { getUserProfile, type Client, type InvitedRoom, type RoomSummary } from "~/lib/matrix";
import { formatUntil } from "~/lib/relative-time";
import { sortByActivity } from "~/lib/room-utils";
import { ICON_STROKE } from "~/lib/icon";

/** Sidebar render width. `full` is the default room list; `rail` is the
 *  collapsed icon rail the narrow embed (400–560px) renders instead. */
export type SidebarVariant = "full" | "rail";

interface SidebarProps {
  /** Render width. Defaults to `full`; `rail` shows the collapsed icon
   *  rail of room avatars. */
  variant?: SidebarVariant;
  /** Stretch the full room list to the container width instead of the
   *  fixed sidebar width. Set when the sidebar is the embed's sole
   *  column (sub-400 drill-down) so the list fills the surface. */
  fill?: boolean;
  /** Open the create-room dialog. Both the Channels-section "+"
   *  and the empty-state CTA call this. Sidebars outside the app
   *  shell (Storybook) may pass a no-op. */
  onCreateRoom?: () => void;
  /** Open the new-DM dialog. The DMs-section "+" button calls this. */
  onNewDm?: () => void;
  /** Open the global-search palette seeded with a query. The room
   *  list's bridge row, search-box Enter, and ⌘K button route here. */
  onOpenSearch?: (query: string) => void;
  displayName?: string | null;
  user?: SidebarUserProps["user"];
  onOpenSettings?: () => void;
  onSignOut?: () => void;
  logoutHref?: string;
  /** Standalone shell only; omitted in the embed where the host owns the widget. */
  feedbackAction?: SidebarUserProps["feedbackAction"];
}

const SIDEBAR_WIDTH_KEY = "chat.sidebar.width";
const SIDEBAR_BOUND_FALLBACK = { min: 200, max: 360, fallback: 224 };

export function Sidebar({
  variant = "full",
  fill = false,
  onCreateRoom,
  onNewDm,
  onOpenSearch,
  displayName,
  user,
  onOpenSettings,
  onSignOut,
  logoutHref,
  feedbackAction,
}: SidebarProps) {
  const {
    activeView,
    activeOrgSpaceId,
    activeSpaceId,
    client,
    setActiveView,
    activeRoomId,
    setActiveRoomId,
  } = useMatrix();
  const rooms = useMatrixRooms();
  const { items: scheduledItems } = useScheduledMessages();
  const { activityCount, threadsHasUnread } = useAttention();
  const archivedCount = rooms.filter((room) => !room.isSpace && room.archived !== null).length;

  const bounds = useRef(readWidthBounds("--chat-sidebar", SIDEBAR_BOUND_FALLBACK));
  const [width, setWidth] = useState<number>(() => {
    const saved = readPersistedWidth(SIDEBAR_WIDTH_KEY);
    const { min, max, fallback } = bounds.current;
    return saved ? Math.max(min, Math.min(max, saved)) : fallback;
  });
  const [resizing, setResizing] = useState(false);
  const asideRef = useRef<HTMLElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    setResizing(true);
    handleRef.current?.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!resizing || !asideRef.current) return;
    const left = asideRef.current.getBoundingClientRect().left;
    const { min, max } = bounds.current;
    setWidth(Math.max(min, Math.min(max, event.clientX - left)));
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!resizing) return;
    setResizing(false);
    handleRef.current?.releasePointerCapture(event.pointerId);
    persistWidth(SIDEBAR_WIDTH_KEY, width);
  };
  const onPointerCancel = () => {
    if (!resizing) return;
    setResizing(false);
  };
  useEffect(() => {
    if (!resizing) return;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = "";
    };
  }, [resizing]);
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 40 : 10;
    const { min, max, fallback } = bounds.current;
    let next = width;
    if (event.key === "ArrowLeft") next = width - step;
    else if (event.key === "ArrowRight") next = width + step;
    else if (event.key === "Home") next = min;
    else if (event.key === "End") next = max;
    else if (event.key === "Enter") next = fallback;
    else return;
    event.preventDefault();
    next = Math.max(min, Math.min(max, next));
    setWidth(next);
    persistWidth(SIDEBAR_WIDTH_KEY, next);
  };

  if (variant === "rail") {
    return (
      <SidebarRail
        rooms={rooms}
        activeRoomId={activeRoomId}
        roomIsActiveView={activeView === "room"}
        onSelect={setActiveRoomId}
      />
    );
  }

  return (
    <aside
      ref={asideRef}
      data-testid="sidebar"
      style={{
        width: fill ? "100%" : width,
        height: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        background: "var(--chat-surface-panel)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ActivityBar
          activityCount={activityCount}
          threadsHasUnread={threadsHasUnread}
          scheduledCount={scheduledItems.length}
          activeView={activeView}
          onOpenUnreads={() => setActiveView("unreads")}
          onOpenActivity={() => setActiveView("mentions")}
          onOpenThreads={() => setActiveView("threads")}
          onOpenSaved={() => setActiveView("saved")}
          onOpenScheduled={() => setActiveView("scheduled")}
        />
        <InvitationsSection client={client} onSelectRoom={setActiveRoomId} />
        <RoomList
          activeOrgSpaceId={activeOrgSpaceId}
          activeSpaceId={activeSpaceId}
          onCreateRoom={onCreateRoom}
          onNewDm={onNewDm}
          onOpenSearch={onOpenSearch}
        />
        {archivedCount > 0 && (
          <ArchivedFooterLink
            active={activeView === "archived"}
            onOpen={() => setActiveView("archived")}
          />
        )}
      </div>
      <SidebarFooter
        displayName={displayName ?? null}
        user={user}
        onOpenSettings={onOpenSettings}
        onSignOut={onSignOut}
        logoutHref={logoutHref}
        feedbackAction={feedbackAction}
      />
      {!fill && (
        <div
          ref={handleRef}
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuemin={bounds.current.min}
          aria-valuemax={bounds.current.max}
          aria-valuenow={Math.round(width)}
          className="chat-sidebar-resize-handle"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onDoubleClick={() => {
            setWidth(bounds.current.fallback);
            persistWidth(SIDEBAR_WIDTH_KEY, bounds.current.fallback);
          }}
          onKeyDown={onKeyDown}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: "col-resize",
            zIndex: 1,
          }}
        />
      )}
    </aside>
  );
}

/* ─── icon rail ──────────────────────────────────────────────── */

/**
 * Collapsed room rail for the narrow embed (400–560px). Selection flows
 * through the same `MatrixContext` setter the full room list uses, so
 * there is no parallel selection state.
 */
function SidebarRail({
  rooms,
  activeRoomId,
  roomIsActiveView,
  onSelect,
}: {
  rooms: RoomSummary[];
  activeRoomId: string | null;
  roomIsActiveView: boolean;
  onSelect: (id: string) => void;
}) {
  const railRooms = useMemo(
    () => sortByActivity(rooms.filter((room) => !room.isSpace && room.archived === null)),
    [rooms],
  );
  return (
    <nav
      data-testid="sidebar-rail"
      aria-label="Rooms"
      style={{
        width: "var(--chat-sidebar-collapsed-width)",
        height: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        padding: "var(--chat-space-2) 0",
        background: "var(--chat-surface-panel)",
        borderRight: "1px solid var(--chat-surface-border)",
        overflowY: "auto",
      }}
    >
      {railRooms.map((room) => (
        <RailRoomButton
          key={room.id}
          room={room}
          active={room.id === activeRoomId && roomIsActiveView}
          onSelect={() => onSelect(room.id)}
        />
      ))}
    </nav>
  );
}

function RailRoomButton({
  room,
  active,
  onSelect,
}: {
  room: RoomSummary;
  active: boolean;
  onSelect: () => void;
}) {
  const isUnread = room.unreadTotal > 0;
  const isMention = room.unreadHighlight > 0;
  const initials = getInitials(room.name) || (room.isDirect ? "" : "#");
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={room.name}
      aria-current={active ? "true" : undefined}
      title={room.name}
      className="chat-button"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 36,
        height: 36,
        flexShrink: 0,
        border: "none",
        borderRadius: room.isDirect ? "var(--chat-radius-full)" : "var(--chat-radius-md)",
        background: active ? "var(--chat-surface-raised)" : "var(--chat-surface-app)",
        color: active || isUnread ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: 12,
        fontWeight: 400,
        cursor: "pointer",
        padding: 0,
      }}
    >
      <span data-feedback-redact="true">{initials || "–"}</span>
      {isUnread ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            width: 8,
            height: 8,
            borderRadius: "var(--chat-radius-full)",
            background: isMention ? "var(--chat-accent)" : "var(--chat-fg-muted)",
          }}
        />
      ) : null}
    </button>
  );
}

/* ─── sub-components ─────────────────────────────────────────── */

function useInviterLabel(client: Client | null, inviter: string): string {
  const [label, setLabel] = useState(inviter);
  useEffect(() => {
    setLabel(inviter);
    if (!client) return;
    let active = true;
    getUserProfile(client, inviter)
      .then((profile) => {
        if (!active) return;
        const name = profile.displayName?.trim();
        setLabel(name || inviter);
      })
      .catch(() => {
        if (active) setLabel(inviter);
      });
    return () => {
      active = false;
    };
  }, [client, inviter]);
  return label;
}

function InvitationsSection({
  client,
  onSelectRoom,
}: {
  client: Client | null;
  onSelectRoom: (roomId: string) => void;
}) {
  const { invites, pending, error, accept, decline } = useInvitations(client, onSelectRoom);

  if (!client || invites.length === 0) return null;

  return (
    <section
      className="chat-sidebar-section"
      style={{ padding: "var(--chat-space-4) 0 0" }}
      aria-label="Invitations"
    >
      <ActivitySectionHeader label="Invitations" />
      <div
        style={{
          padding: "0 var(--chat-space-2)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-2)",
        }}
      >
        {invites.map((invite) => (
          <InvitationRow
            key={invite.roomId}
            invite={invite}
            client={client}
            pendingAction={pending.get(invite.roomId) ?? null}
            errorMessage={error?.roomId === invite.roomId ? error.message : null}
            onAccept={() => void accept(invite.roomId)}
            onDecline={() => void decline(invite.roomId)}
          />
        ))}
      </div>
    </section>
  );
}

function InvitationRow({
  invite,
  client,
  pendingAction,
  errorMessage,
  onAccept,
  onDecline,
}: {
  invite: InvitedRoom;
  client: Client | null;
  pendingAction: "accept" | "decline" | null;
  errorMessage: string | null;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const inviterName = useInviterLabel(client, invite.inviter);
  const name = invite.name ?? (invite.isDirect ? invite.inviter : invite.roomId);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-1)",
        padding: "var(--chat-space-2) var(--chat-space-3)",
      }}
    >
      <span
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          fontWeight: 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <span
        style={{
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        Invited by {inviterName}
        {invite.encrypted ? " - Encrypted" : ""}
      </span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          paddingTop: "var(--chat-space-1)",
        }}
      >
        <InvitationActionButton onClick={onAccept} disabled={pendingAction !== null}>
          Accept
        </InvitationActionButton>
        <button
          type="button"
          onClick={onDecline}
          disabled={pendingAction !== null}
          className="chat-button"
          style={{
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            padding: 0,
            cursor: pendingAction ? "default" : "pointer",
          }}
        >
          Decline
        </button>
      </div>
      {errorMessage ? (
        <span
          role="status"
          style={{
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
          }}
        >
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
}

function InvitationActionButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="chat-button"
      style={{
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: disabled ? "transparent" : "var(--chat-fg-primary)",
        color: disabled ? "var(--chat-fg-muted)" : "var(--chat-surface-app)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
        padding: "var(--chat-space-1) var(--chat-space-2)",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

function SidebarFooter({
  displayName,
  user,
  onOpenSettings,
  onSignOut,
  logoutHref,
  feedbackAction,
}: {
  displayName: string | null;
  user?: SidebarUserProps["user"];
  onOpenSettings?: () => void;
  onSignOut?: () => void;
  logoutHref?: string;
  feedbackAction?: SidebarUserProps["feedbackAction"];
}) {
  const dnd = useDnd();
  const portalRoot = usePortalRoot();
  const { client, userId, roomsReady, canUnlockHistory, resumeRecovery } = useMatrix();
  const { consoleUrl } = useEmbedRuntime();
  const { choice, setTheme } = useNurionTheme();
  const { status, setStatus } = useMyStatus(client);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [dndOpen, setDndOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const dndAnchorRef = useRef<HTMLButtonElement | null>(null);
  const statusTriggerRef = useRef<HTMLButtonElement | null>(null);
  const statusTriggerId = useId();
  const accountName = user?.name ?? null;
  const localpart = localpartOf(userId);
  const email = user?.email ?? emailFromMatrixUserId(userId) ?? "Matrix account";
  const [profile, setProfile] = useState<{
    client: Client | null;
    userId: string | null;
    displayName: string | null;
    ready: boolean;
  }>(() => ({
    client,
    userId,
    displayName: displayName ?? null,
    ready: false,
  }));
  const profileCurrent = profile.client === client && profile.userId === userId;
  const profileReady = profileCurrent && profile.ready;
  const globalDisplayName = profileCurrent ? profile.displayName : null;
  const preferredName = preferredAccountLabel(accountName, globalDisplayName, localpart);
  const ownMembers = useOwnMemberNames(!preferredName && profileReady);
  const resolved = resolveAccountLabel({
    accountName,
    globalDisplayName,
    memberNames: ownMembers.names,
    localpart,
  });
  const membersSettled =
    !ownMembers.pending || mostFrequentName(ownMembers.names, localpart) !== null;
  const trimmedGlobal = globalDisplayName?.trim();
  const hasGlobalName = Boolean(trimmedGlobal) && trimmedGlobal !== localpart;
  const ready = profileReady && roomsReady && (hasGlobalName || membersSettled);
  const hasAccountName = Boolean(accountName?.trim());
  const shownName = hasAccountName || ready ? resolved : null;
  const avatarMxc = avatarUrl?.startsWith("mxc://") ? avatarUrl : null;
  const avatarMedia = useAuthenticatedMedia(client, avatarMxc);
  const theme = choice === null ? "system" : choice;
  const accountMenuCollisionBoundary =
    portalRoot && typeof document !== "undefined" && portalRoot !== document.body
      ? portalRoot
      : undefined;
  const products = toSidebarProducts({
    consoleUrl,
    current: "chat",
  });
  const closePicker = () => {
    setPickerOpen(false);
    statusTriggerRef.current?.focus();
  };

  useEffect(() => {
    if (!client || !userId) {
      setAvatarUrl(null);
      setProfile({ client, userId, displayName: null, ready: true });
      return;
    }
    let cancelled = false;
    setAvatarUrl(null);
    setProfile({ client, userId, displayName: null, ready: false });
    void getUserProfile(client, userId)
      .then((result) => {
        if (cancelled) return;
        setAvatarUrl(result.avatarUrl);
        setProfile({ client, userId, displayName: result.displayName, ready: true });
      })
      .catch(() => {
        if (!cancelled) setProfile({ client, userId, displayName: null, ready: true });
      });
    return () => {
      cancelled = true;
    };
  }, [client, userId]);

  return (
    <div
      style={{
        flexShrink: 0,
        borderTop: "1px solid var(--chat-surface-border)",
        padding: "var(--chat-space-2) var(--chat-space-3)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-1)",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: "var(--chat-space-1)", width: "100%" }}
      >
        <div onPointerDown={(event) => event.stopPropagation()} style={{ minWidth: 0, flex: 1 }}>
          <button
            id={statusTriggerId}
            ref={statusTriggerRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((open) => !open)}
            className="chat-button hover:bg-[var(--chat-surface-raised)]"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-2)",
              width: "100%",
              height: 28,
              padding: "0 var(--chat-space-2)",
              border: "1px solid var(--chat-surface-border)",
              borderRadius: "var(--chat-radius-md)",
              background: "transparent",
              color: status ? "var(--chat-fg-secondary)" : "var(--chat-fg-muted)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-xs-size)",
              lineHeight: "var(--chat-type-body-xs-line)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            {status ? (
              <span aria-hidden style={{ fontSize: 14, lineHeight: "14px" }}>
                {status.emoji}
              </span>
            ) : (
              <Smile aria-hidden size={14} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
            )}
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {status ? status.message : "Set a status"}
              {status?.expiresAt ? ` · until ${formatUntil(new Date(status.expiresAt))}` : ""}
            </span>
          </button>
          {pickerOpen ? (
            <StatusPicker
              anchor={statusTriggerRef.current}
              status={status}
              onSetStatus={setStatus}
              onClear={() => setStatus(null)}
              onClose={closePicker}
              labelledBy={statusTriggerId}
            />
          ) : null}
        </div>
        <IconTooltip
          label={dnd.active && dnd.until ? `Until ${formatUntil(dnd.until)}` : "Do not disturb"}
        >
          <button
            ref={dndAnchorRef}
            type="button"
            onClick={() => setDndOpen((open) => !open)}
            aria-label={dnd.active ? "Do not disturb is on" : "Do not disturb"}
            aria-expanded={dndOpen}
            aria-haspopup="dialog"
            aria-pressed={dnd.active}
            className="chat-button hover:bg-[var(--chat-surface-raised)]"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              border: "none",
              borderRadius: "var(--chat-radius-md)",
              background: "transparent",
              color: dnd.active ? "var(--chat-accent)" : "var(--chat-fg-muted)",
              padding: 0,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <DndIcon aria-hidden size={14} strokeWidth={ICON_STROKE} />
          </button>
        </IconTooltip>
      </div>

      <SidebarUser
        user={{
          name: shownName,
          email,
          avatarUrl: user?.avatarUrl ?? avatarMedia.src ?? undefined,
        }}
        links={{
          consoleAccountUrl: `${consoleUrl}/profile`,
          consoleTeamUrl: `${consoleUrl}/team`,
          consoleBillingUrl: `${consoleUrl}/billing`,
          consoleSupportUrl: `${consoleUrl}/support?origin=chat`,
          consoleHelpUrl: `${consoleUrl}/faq?origin=chat`,
          products,
          logoutHref: logoutHref ?? "/logout",
        }}
        feedbackAction={feedbackAction}
        attentionAction={
          canUnlockHistory
            ? { label: "Unlock your messages", Icon: KeyRound, onSelect: resumeRecovery }
            : null
        }
        inAppSettings={onOpenSettings ? { label: "Settings", onSelect: onOpenSettings } : null}
        theme={theme}
        onThemeChange={(next) => setTheme(next === "system" ? null : next)}
        onSignOut={onSignOut}
        align="start"
        side="top"
        menuPortalContainer={portalRoot}
        menuCollisionBoundary={accountMenuCollisionBoundary}
        menuContentClassName="z-[70] bg-[var(--chat-surface-overlay)] text-[var(--chat-fg-primary)] backdrop-blur-none"
        menuSubContentClassName="z-[70] bg-[var(--chat-surface-overlay)] text-[var(--chat-fg-primary)] backdrop-blur-none"
      />

      {dndOpen ? (
        <DndMenu
          anchor={dndAnchorRef.current}
          data={dnd.data}
          active={dnd.active}
          onOff={dnd.setOff}
          onManualUntil={dnd.setManualUntil}
          onSchedule={dnd.setSchedule}
          onClose={() => setDndOpen(false)}
        />
      ) : null}
    </div>
  );
}

function emailFromMatrixUserId(userId: string | null): string | null {
  if (!userId) return null;
  const localpart = /^@([^:]+):/.exec(userId)?.[1] ?? userId;
  return localpart.replace(/=40/g, "@").replace(/=2e/g, ".");
}

function ArchivedFooterLink({ active, onOpen }: { active: boolean; onOpen: () => void }) {
  return (
    <div
      style={{
        flexShrink: 0,
        padding: "var(--chat-space-2)",
        borderTop: "1px solid var(--chat-surface-border)",
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-pressed={active}
        className="chat-button"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          width: "100%",
          height: "var(--chat-row-comfortable-channel)",
          padding: "0 var(--chat-space-3)",
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: active ? "var(--chat-surface-raised)" : "transparent",
          color: active ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <Archive size={14} strokeWidth={ICON_STROKE} aria-hidden />
        <span>Archived channels</span>
      </button>
    </div>
  );
}

/* ─── Activity section ───────────────────────────────────────── */

function ActivitySectionHeader({ label }: { label: string }) {
  return (
    <div
      className="chat-sidebar-activity-header"
      style={{
        display: "flex",
        alignItems: "center",
        height: "var(--chat-sidebar-section-header-height)",
        // Match the room-list section header inset so ACTIVITY,
        // CHANNELS and DIRECT MESSAGES sit on the same vertical axis.
        padding: "0 var(--chat-space-3)",
        color: "var(--chat-fg-readable)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: 10,
        lineHeight: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
    </div>
  );
}
